import {
    AfterViewInit,
    Component,
    inject,
    Input,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';

import { ApplicationFormFieldComponent } from '../../../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, takeUntil, tap } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { MatButtonModule } from '@angular/material/button';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ScheduleDStepOneHelperMessages } from './constants/schedule-d-step-one-helper-messages';
import { ScheduleDStepOneRegExPattern } from './models/schedule-d-step-one-regex-pattern';
import { ScheduleDStepOneExternalLinks } from './constants/schedule-d-step-one-external-links';
import { ScheduleDStepBaseComponent } from '../schedule-d-step-base/schedule-d-step-base.component';
import { ScheduleDFormService } from '../../services/schedule-d-form/schedule-d-form.service';

import { FormControlService } from '../../../../../../core/services/form-control/form-control.service';
import { ScheduleD } from '../../models/schedule-d';
import { FieldDeselector } from '../../../../../../core/models/field-deselector';
import { LocationTypeCodes } from '../../../../shared/constants/location-type-codes';
import { AreaOfOperationCodes } from '../../constants/area-of-operation-code-constants';

@Component({
    selector: 'app-schedule-d-step-one',
    imports: [
        MatSelectModule,
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        FormsModule,
        MatInputModule,
        MatButtonModule,
        ReactiveFormsModule,
        RegexInputDirective,
        MatIconModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './schedule-d-step-one.component.html',
})
export class ScheduleDStepOneComponent
    extends ScheduleDStepBaseComponent
    implements OnInit, AfterViewInit
{
    @Input() atMaximumFixedLocations: boolean = false;
    @ViewChild('locationTypeField') locationTypeField: MatSelect | undefined;

    messages = ScheduleDStepOneHelperMessages;

    yesNoOptions: DropdownOption[] = [];
    locationTypes: DropdownOption[] = [];
    filteredAreaOfOperationCodes: DropdownOption[] = [];

    private areaOfOperationCodes: DropdownOption[] = [];

    /**
     * All location types but without the fixed option.
     * We only want to show the fixed location option when
     */
    locationTypesWithoutFixedOption: DropdownOption[] = [];

    regExPattern = RegExPattern;
    scheduleDStepOneRegExPattern = ScheduleDStepOneRegExPattern;
    readonly dirtyErrorStateMatcher = new ShowOnDirtyErrorStateMatcher();

    externalLinks = ScheduleDStepOneExternalLinks;

    private readonly dependentFormControlsForResetOptions: FieldDeselector<ScheduleD> =
        {
            locationTypeCode: {
                dependentFields: [
                    'areaOfOperationCode',
                    'locationName',
                    'towerRegistrationNumber',
                    'requireEWAToRegisterTower',
                ],
            },
        };

    private readonly dropdownService = inject(DropdownService);
    private readonly formService = inject(ScheduleDFormService);
    private readonly formDeselectService = inject(FormControlService);

    ngOnInit(): void {
        this.loadDropdownData();
        this.setupFormDeselectors();
        this.setupFormControlListeners();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.locationTypeField?.focus();
        });
    }

    getLocationTypes(): DropdownOption[] {
        if (this.shouldShowFixedLocationType()) {
            return this.locationTypes;
        }

        return this.locationTypesWithoutFixedOption;
    }

    shouldShowFixedLocationType() {
        return (
            this.atMaximumFixedLocations === false || this.showFixedControls()
        );
    }

    showFixedControls() {
        return this.formService.isFixed(this.form);
    }

    showNonFixedControls() {
        return this.formService.isNonFixed(this.form);
    }

    /**
     * Sets up listeners for form control changes
     */
    private setupFormControlListeners(): void {
        this.setupCurrentLocationTypeFormListener();
    }

    /**
     * Sets up listener and initial filtering logic for the Location Type field.
     * Ensures Area of Operation Code is filtered both on load and on user changes.
     */
    private setupCurrentLocationTypeFormListener(): void {
        const currentLocationType = this.form.controls.locationTypeCode;

        // Filter on initial load
        this.filterAreaCodeByLocationType(currentLocationType.value);

        // Filter on user change
        currentLocationType.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((locationTypeCode) =>
                    this.filterAreaCodeByLocationType(locationTypeCode),
                ),
            )
            .subscribe();
    }

    private setupFormDeselectors() {
        this.formDeselectService.setupFormDeselectorsHandler(
            this.form,
            this.dependentFormControlsForResetOptions,
            this.destroy$,
        );
    }

    private loadDropdownData() {
        forkJoin({
            yesNoOptions: this.dropdownService.getYesNoOptions(),
            locationTypes: this.dropdownService.getLocationTypes(),
            areaOfOperationCodes:
                this.dropdownService.getAreaOfOperationCodes(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        yesNoOptions: yesNoOptions,
                        locationTypes: locationTypes,
                        areaOfOperationCodes: areaOfOperationCodes,
                    }) => {
                        this.yesNoOptions = yesNoOptions;
                        this.locationTypes = locationTypes;
                        this.filteredAreaOfOperationCodes =
                            areaOfOperationCodes;
                        // Create a distinct copy to avoid mutating the original list when filtering
                        this.areaOfOperationCodes = [...areaOfOperationCodes];

                        this.locationTypesWithoutFixedOption =
                            this.getLocationTypesWithoutFixed(locationTypes);
                    },
                ),
            )
            .subscribe();
    }

    private getLocationTypesWithoutFixed(locationTypes: DropdownOption[]) {
        return locationTypes.filter(
            (option) => option.value !== LocationTypeCodes.fixed,
        );
    }

    /**
     * Filters Area of Operation Code dropdown based on location type.
     * - Restricts to 'X' if location type is Meter Control Station (6).
     * - Restores full list otherwise.
     * - Auto-selects 'X' if it's the only valid option.
     */
    private filterAreaCodeByLocationType(
        locationTypeCode: string | null,
    ): void {
        if (
            locationTypeCode === null ||
            locationTypeCode != LocationTypeCodes.meterControlStation
        ) {
            this.filteredAreaOfOperationCodes = [...this.areaOfOperationCodes];
            return;
        }

        const landMobileOperationCode =
            AreaOfOperationCodes.landMobileControlStation;

        this.filteredAreaOfOperationCodes = this.areaOfOperationCodes.filter(
            (option) => option.value === landMobileOperationCode,
        );

        const control = this.form.controls.areaOfOperationCode;

        // Pre-select 'X - Land Mobile Control Station' when it's the only option available
        if (
            this.filteredAreaOfOperationCodes.length === 1 &&
            this.filteredAreaOfOperationCodes[0].value ===
                landMobileOperationCode
        ) {
            control.setValue(landMobileOperationCode);
        } else {
            control.setValue(null);
        }
    }
}
